<script setup lang="ts">
import type { PageArticle } from '~/types/Article'
import { API_ARTICLE_BY_SLUG } from '~/utils/constants'

const route = useRoute()
const { t, locale } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const { localizeString } = useLocalization()
const dayjs = useDayjs()

const articleSlug = route.params.slug as string

const {
  data: article,
  status,
  error,
} = useFetch<PageArticle>(
  API_ARTICLE_BY_SLUG.replace('{slug}', `${articleSlug}`),
  {
    params: {
      locale: locale.value || 'en',
    },
    key: `article-${articleSlug}-${locale.value}`,
  }
)

const currentArticle = computed(() => article.value?.currentArticle)
const previousArticle = computed(() => article.value?.previousArticle)
const nextArticle = computed(() => article.value?.nextArticle)

const formatDate = computed(() => {
  return localizeString(
    dayjs(currentArticle.value?.publishedAt).format('D MMM, YYYY'),
    dayjs(currentArticle.value?.publishedAt).format('YYYY年MM月DD日')
  )
})

useJsonLd({
  article: article.value?.currentArticle,
})

useSeoMeta({
  title: () => currentArticle.value?.title + ' - ' + t('app.title'),
  description: () => currentArticle.value?.description,
  ogTitle: () => currentArticle.value?.title + ' - ' + t('app.title'),
  ogImage: () => currentArticle.value?.cover,
  ogUrl: () => config.public.baseUrl + route.path,
  ogDescription: () => currentArticle.value?.description,
  twitterTitle: () => currentArticle.value?.title + ' - ' + t('app.title'),
  twitterImage: () => currentArticle.value?.cover,
  twitterDescription: () => currentArticle.value?.description,
  twitterCard: () => 'summary_large_image',
})
</script>

<template>
  <UContainer>
    <UPage
      :ui="{
        center: {
          wrapper: 'flex flex-col lg:grid lg:grid-cols-10 lg:gap-32',
          narrow: 'lg:col-span-6',
          base: 'lg:col-span-7',
          full: 'lg:col-span-10',
        },
        right: 'lg:col-span-3',
      }"
    >
      <!-- Skeleton loader -->
      <template v-if="status === 'pending'">
        <div class="mt-8 space-y-4">
          <!-- Title skeleton -->
          <div
            class="h-8 bg-neutral-200 rounded-md dark:bg-neutral-700 w-3/4"
          ></div>
          <!-- Description skeleton -->
          <div
            class="h-4 bg-neutral-200 rounded-md dark:bg-neutral-700 w-1/2"
          ></div>
        </div>

        <!-- Image/Video skeleton -->
        <div class="relative aspect-w-16 aspect-h-9 mt-8 mb-8">
          <div
            class="w-full h-full bg-gray-200 rounded-md dark:bg-gray-700 animate-pulse"
          ></div>
        </div>

        <!-- Content skeleton -->
        <div class="space-y-4">
          <div
            class="h-4 bg-neutral-200 rounded-md dark:bg-neutral-700 w-full"
          ></div>
          <div
            class="h-4 bg-neutral-200 rounded-md dark:bg-neutral-700 w-5/6"
          ></div>
          <div
            class="h-4 bg-neutral-200 rounded-md dark:bg-neutral-700 w-4/6"
          ></div>
        </div>
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="text-center py-8">
        <UIcon
          name="i-tabler-exclamation-circle"
          class="text-red-500 w-12 h-12 mx-auto mb-4"
        />
        <p class="text-lg font-semibold">
          {{ error.statusMessage || 'Error loading article' }}
        </p>

        <p class="text-gray-600 dark:text-gray-400">
          {{ error.message || 'Please try again later' }}
        </p>
      </div>

      <!-- Actual content -->
      <template v-else-if="currentArticle">
        <UBreadcrumb
          :links="[
            {
              label: '',
              to: localePath('/'),
              icon: 'i-tabler-home',
            },

            {
              label: t('articles.title'),
              to: localePath('/articles'),
            },
            {
              label: currentArticle.title,
            },
          ]"
        />
        <div class="flex items-center justify-between mt-4 sm:mt-6 lg:mt-8">
          <ULink
            v-if="currentArticle?.article_category"
            :to="
              localePath(
                `/articles/category/${currentArticle.article_category.slug}`
              )
            "
          >
            <UBadge
              color="sky"
              variant="subtle"
              :label="currentArticle.article_category.name"
              class="hover:bg-sky-100 dark:hover:bg-sky-900 transition-colors duration-500"
            />
          </ULink>
          <div>
            <time
              :datetime="formatDate"
              :title="formatDate"
              class="text-base text-neutral-500 dark:text-neutral-400"
              aria-label="Published date"
            >
              {{ formatDate }}
            </time>
          </div>
        </div>
        <UPageHeader
          :title="currentArticle.title"
          :description="currentArticle.description"
        />

        <div class="relative aspect-w-16 aspect-h-9 mb-8">
          <div
            v-if="
              currentArticle.video && typeof currentArticle.video === 'string'
            "
            class="absolute inset-0"
          >
            <video
              ref="videoPlayer"
              :poster="currentArticle.cover"
              controls
              preload="metadata"
              class="w-full h-full object-cover"
            >
              <source :src="currentArticle.video" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <img
            v-else
            :src="currentArticle.cover"
            :alt="currentArticle.alt || ''"
            class="w-full aspect-[1200/630] rounded-xl"
          />
        </div>

        <ArticleTableOfContents
          v-if="article"
          :content="currentArticle.content"
          :title="currentArticle.title"
          class="lg:hidden"
        />

        <UPageBody prose>
          <MDC v-if="currentArticle" :value="currentArticle.content" />

          <!-- related article -->
          <ArticleRelated
            :articles="currentArticle.related_articles"
            :title="t('articles.relatedArticles')"
          />

          <ArticleSurround
            :previousArticle="nextArticle ?? null"
            :nextArticle="previousArticle ?? null"
          />
        </UPageBody>
      </template>

      <!-- Right sidebar -->
      <template #right>
        <ArticleTableOfContents
          v-if="currentArticle"
          :content="currentArticle.content"
          :title="currentArticle.title"
          class="hidden lg:block"
        />
      </template>
    </UPage>
  </UContainer>
</template>

<style scoped></style>
